import type {
	AIResponsePayload,
	AssessmentRunStatus,
	BaseItemSchema,
	OriginalItemTypes,
} from "@memorang/types";
import { isPteFeedbackAgentItem } from "../constants/pte/feedback";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";

const CHOICE_RESPONSE_TYPES = new Set([
	"choice",
	"OPTION_SELECTION",
	"INLINE_TEXT_SELECTION",
]);

// FIBDDR (TOKEN_MAPPING) / FIBDPR (INLINE_OPTION_SELECTION) store a per-blank
// answer map; FIBTIL shares SHORT_TEXT_ENTRY with WFDL, so it's disambiguated
// by itemType below rather than by response type alone.
const BLANK_MAP_RESPONSE_TYPES = new Set([
	"TOKEN_MAPPING",
	"INLINE_OPTION_SELECTION",
]);

// Map HIWL `word-<index>` ids to actual words for agent feedback.
export const resolveHighlightedWords = (
	item: BaseItemSchema,
	selectedChoiceIds: string[],
): string[] => {
	const words = (item.content?.stem?.text ?? "").split(/\s+/).filter(Boolean);
	return selectedChoiceIds
		.map((id) => words[Number(id.replace("word-", ""))])
		.filter((word): word is string => Boolean(word));
};

const resolveChoiceResponse = (
	item: BaseItemSchema,
	itemId: number,
	responseType: string,
): string[] => {
	const selectedChoiceIds =
		useSessionStore.getState().itemStates?.get(itemId)?.selectedChoiceIds ??
		item.answer?.selectedChoiceIds ??
		[];
	return responseType === "INLINE_TEXT_SELECTION"
		? resolveHighlightedWords(item, selectedChoiceIds)
		: selectedChoiceIds;
};

const isBlankMapResponse = (item: BaseItemSchema, responseType?: string) => {
	const itemType = item.metadata?.type;
	return (
		(responseType != null && BLANK_MAP_RESPONSE_TYPES.has(responseType)) ||
		(responseType === "SHORT_TEXT_ENTRY" &&
			(itemType === "FIBTIL" || itemType === "FIBTIL_CORE"))
	);
};

const resolveOrderResponse = (item: BaseItemSchema, itemId: number) => {
	const raw = useAIFeedbackStore.getState().userResponse?.[itemId];
	return Array.isArray(raw) ? raw.map(String) : item.answer?.selectedOrder;
};

// Feedback items store their answer in different slots, mirroring
// handlePostAnswer's own resolution so resume (before the item's container
// re-hydrates useAIFeedbackStore) reads the same fallback as the deterministic
// scoring path does:
//   - HCSL/HIWL: itemStates.selectedChoiceIds, falling back to item.answer.selectedChoiceIds
//   - FIBDDR/FIBDPR/FIBTIL: useAIFeedbackStore's blank-value map, falling back to item.answer.selectedBlankValues
//   - ROPR: useAIFeedbackStore's ordered array, falling back to item.answer.selectedOrder
//   - WFDL: useAIFeedbackStore's typed text, falling back to item.answer.userResponseText
export const resolveFeedbackUserResponse = (item: BaseItemSchema): unknown => {
	const itemId = item.id;
	if (itemId == null) {
		return undefined;
	}
	const responseType = item.response?.type;

	if (responseType && CHOICE_RESPONSE_TYPES.has(responseType)) {
		return resolveChoiceResponse(item, itemId, responseType);
	}
	if (isBlankMapResponse(item, responseType)) {
		return (
			useAIFeedbackStore.getState().userResponse?.[itemId] ??
			item.answer?.selectedBlankValues
		);
	}
	if (responseType === "ORDER_RECONSTRUCTION") {
		return resolveOrderResponse(item, itemId);
	}
	return (
		useAIFeedbackStore.getState().userResponse?.[itemId] ??
		item.answer?.userResponseText
	);
};

export const serializeFeedbackUserResponse = (value: unknown): string => {
	if (typeof value === "string") {
		return value;
	}
	if (Array.isArray(value)) {
		return value.length ? JSON.stringify(value) : "";
	}
	if (value && typeof value === "object") {
		return Object.keys(value).length ? JSON.stringify(value) : "";
	}
	return "";
};

export const hasCapturedFeedbackResponse = (item: BaseItemSchema): boolean =>
	serializeFeedbackUserResponse(resolveFeedbackUserResponse(item)).trim()
		.length > 0;

export const toFeedbackAiResponse = (params: {
	feedback: string;
	answer: string;
	status: AssessmentRunStatus;
	runDetails?: { runId?: string; threadId?: string; assistantId?: string };
}): AIResponsePayload => ({
	feedback: params.feedback,
	subskillAssessments: [],
	ai_run_id: params.runDetails?.runId ?? "",
	ai_thread_id: params.runDetails?.threadId ?? "",
	metadata: { assistantId: params.runDetails?.assistantId ?? "" },
	answer: params.answer,
	status: params.status,
});

// Re-posts (revisits, edits before the re-grade completes) must never wipe an
// already-earned COMPLETE blob — reuse it verbatim until a newer run supersedes it.
export const buildFeedbackAiResponse = (
	item: BaseItemSchema,
): AIResponsePayload => {
	const itemId = item.id!;
	const existing = useAIFeedbackStore.getState().aiResponse?.[itemId];
	const serializedResponse = serializeFeedbackUserResponse(
		resolveFeedbackUserResponse(item),
	);

	if (existing?.status === "COMPLETE") {
		return toFeedbackAiResponse({
			feedback: existing.explanation ?? "",
			answer: existing.answer ?? serializedResponse,
			status: "COMPLETE",
			runDetails: existing.runDetails,
		});
	}

	return toFeedbackAiResponse({
		feedback: "",
		answer: serializedResponse,
		status: "PENDING",
	});
};

// getEarnedPoints has historically had gaps for some feedback item types (no
// scoring branch matched -> 0 despite a correct answer). Today that's masked
// server-side by a `maximumScore` fallback that only fires when aiResponse is
// absent (services createResponseRecord); attaching aiResponse removes that
// rescue. This is a safety net keyed off the bug's exact signature — a correct
// answer with zero computed points — rather than a hardcoded type allowlist,
// so it self-corrects as scoring support for more types lands (and never
// clobbers genuine partial credit, since that's never both correct and zero).
// See docs/plans/pte-summative-dynamic-feedback.md §3.
export const resolveFeedbackPoints = (
	itemType: OriginalItemTypes | undefined,
	isCorrect: boolean | undefined,
	computedPoints: number | undefined,
	maximumScore: number | undefined,
): number | undefined => {
	if (!isPteFeedbackAgentItem(itemType)) {
		return computedPoints;
	}
	if (isCorrect && !computedPoints) {
		return maximumScore ?? 0;
	}
	return computedPoints;
};
